import React, { useEffect, useMemo, useState } from "react";
import {
  statsClientes,
  updatePatientProfessionalStatus,
} from "../api";
import "./ClienteMesTab.css";

function fmtDias(d) {
  if (d === null || d === undefined) return "—";
  return `${d}d`;
}

function fmtFecha(value) {
  if (!value) return "—";

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("es-ES");
}

function Badge({ tipo }) {
  const style = {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid #ddd",
    background: "#f7f7f7",
  };

  if (tipo === "nuevo_sin_seguimiento") {
    return <span style={style}>🆕 Nuevo</span>;
  }

  if (tipo === "riesgo") {
    return <span style={style}>⚠️ Riesgo</span>;
  }

  if (tipo === "inactivo") {
    return <span style={style}>🧊 Inactivo</span>;
  }

  return <span style={style}>✅ Activo</span>;
}

function FidelizacionBadge({ estado }) {
  const style = {
    display: "inline-block",
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid #ddd",
    background: "#f7f7f7",
  };

  const labels = {
    continua: "✅ Continúa",
    abandono: "🧊 Abandono",
    pendiente: "⏳ Pendiente",
    alta_terapeutica: "🎓 Alta terapéutica",
    pausa_temporal: "⏸️ Pausa temporal",
    cambio_profesional: "🔄 Cambio profesional",
    no_inicio: "⛔ No inició",
  };

  return (
    <span style={style}>
      {labels[estado] || estado || "—"}
    </span>
  );
}

export default function ClientesMesTab({ mes, setMes }) {
  const [creados, setCreados] = useState([]);
  const [citas, setCitas] = useState([]);

  const [nuevos, setNuevos] = useState([]);
  const [riesgo, setRiesgo] = useState([]);
  const [inactivos, setInactivos] = useState([]);
  const [activos, setActivos] = useState([]);

  const [fidelizacion, setFidelizacion] = useState([]);

  const [totalCreados, setTotalCreados] = useState(0);
  const [totalCitas, setTotalCitas] = useState(0);

  const [totalNuevos, setTotalNuevos] = useState(0);
  const [totalRiesgo, setTotalRiesgo] = useState(0);
  const [totalInactivos, setTotalInactivos] = useState(0);
  const [totalActivos, setTotalActivos] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [umbralDias, setUmbralDias] = useState(45);
  const [nuevoDias, setNuevoDias] = useState(14);

  const [tab, setTab] = useState("control");

  const [
    profesionalSeleccionado,
    setProfesionalSeleccionado,
  ] = useState("");

  const [
    estadoFidelizacion,
    setEstadoFidelizacion,
  ] = useState("todos");

  const [guardandoEstado, setGuardandoEstado] =
    useState("");

  const [mensajeEstado, setMensajeEstado] =
    useState("");

  /*
   * =========================================================
   * VISTA FIDELIZACIÓN
   * =========================================================
   */

  const fidelizacionView =
    tab === "cartera"
      ? "global"
      : "mensual";

  /*
   * =========================================================
   * CARGAR DATOS
   * =========================================================
   */

  const cargarDatos = async ({
    mostrarLoading = true,
  } = {}) => {
    if (!mes) return;

    if (mostrarLoading) {
      setLoading(true);
    }

    try {
      const data = await statsClientes(mes, {
        umbral_dias: umbralDias,
        nuevo_dias: nuevoDias,
        fidelizacion_view: fidelizacionView,
      });

      setCreados(
        data.creados?.items || []
      );

      setTotalCreados(
        data.creados?.total || 0
      );

      setCitas(
        data.citas?.items || []
      );

      setTotalCitas(
        data.citas?.total || 0
      );

      setNuevos(
        data.nuevos_sin_seguimiento?.items || []
      );

      setTotalNuevos(
        data.nuevos_sin_seguimiento?.total || 0
      );

      setRiesgo(
        data.riesgo?.items || []
      );

      setTotalRiesgo(
        data.riesgo?.total || 0
      );

      setInactivos(
        data.inactivos?.items || []
      );

      setTotalInactivos(
        data.inactivos?.total || 0
      );

      setActivos(
        data.activos?.items || []
      );

      setTotalActivos(
        data.activos?.total || 0
      );

      const items =
        data.fidelizacion_profesionales?.items || [];

      setFidelizacion(items);

      setProfesionalSeleccionado(
        (actual) => {
          if (
            actual &&
            items.some(
              (item) =>
                String(
                  item.professional_id
                ) === String(actual)
            )
          ) {
            return actual;
          }

          return items.length
            ? String(
                items[0].professional_id
              )
            : "";
        }
      );

      setError("");
    } catch (e) {
      setError(
        e?.message ||
          String(e) ||
          "Error al cargar clientes"
      );
    } finally {
      if (mostrarLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [
    mes,
    umbralDias,
    nuevoDias,
    fidelizacionView,
  ]);

  /*
   * =========================================================
   * CAMBIAR ESTADO MANUAL
   * =========================================================
   */

  const cambiarEstadoPaciente = async (
    paciente,
    nuevoEstado
  ) => {
    const key =
      `${paciente.patient_id}-${paciente.professional_id}`;

    setGuardandoEstado(key);
    setMensajeEstado("");

    try {
      await updatePatientProfessionalStatus({
        patient_id: paciente.patient_id,
        professional_id:
          paciente.professional_id,
        status: nuevoEstado,
        notes:
          paciente.manual_notes || "",
      });

      await cargarDatos({
        mostrarLoading: false,
      });

      setMensajeEstado(
        nuevoEstado === "automatico"
          ? "Estado automático restaurado correctamente."
          : "Estado actualizado correctamente."
      );

      window.setTimeout(() => {
        setMensajeEstado("");
      }, 2500);
    } catch (e) {
      setMensajeEstado(
        e?.message ||
          "No se ha podido cambiar el estado."
      );
    } finally {
      setGuardandoEstado("");
    }
  };

  /*
   * =========================================================
   * BUSCADOR
   * =========================================================
   */

  const match = (item) => {
    if (!q.trim()) return true;

    const text =
      `${item.nombre || ""} ${
        item.apellidos || ""
      }`.toLowerCase();

    return text.includes(
      q.trim().toLowerCase()
    );
  };

  const nuevosFiltrados = useMemo(
    () => nuevos.filter(match),
    [nuevos, q]
  );

  const riesgoFiltrado = useMemo(
    () => riesgo.filter(match),
    [riesgo, q]
  );

  const inactivosFiltrados = useMemo(
    () => inactivos.filter(match),
    [inactivos, q]
  );

  const activosFiltrados = useMemo(
    () => activos.filter(match),
    [activos, q]
  );

  /*
   * =========================================================
   * PROFESIONAL ACTUAL
   * =========================================================
   */

  const profesionalActual = useMemo(() => {
    return (
      fidelizacion.find(
        (item) =>
          String(item.professional_id) ===
          String(profesionalSeleccionado)
      ) || null
    );
  }, [
    fidelizacion,
    profesionalSeleccionado,
  ]);

  /*
   * =========================================================
   * PACIENTES FILTRADOS
   * =========================================================
   */

  const pacientesFidelizacionFiltrados =
    useMemo(() => {
      const items =
        profesionalActual?.pacientes || [];

      return items.filter((item) => {
        if (!match(item)) return false;

        if (
          estadoFidelizacion !== "todos" &&
          item.estado !== estadoFidelizacion
        ) {
          return false;
        }

        return true;
      });
    }, [
      profesionalActual,
      estadoFidelizacion,
      q,
    ]);

  /*
   * =========================================================
   * TOTALES CENTRO
   * =========================================================
   */

  const totalFidelizacion = useMemo(() => {
    return fidelizacion.reduce(
      (acc, item) => {
        acc.pacientes += Number(
          item.pacientes_atendidos || 0
        );

        acc.continuan += Number(
          item.continuan || 0
        );

        acc.abandonos += Number(
          item.abandonos || 0
        );

        acc.pendientes += Number(
          item.pendientes || 0
        );

        acc.altas += Number(
          item.altas_terapeuticas || 0
        );

        acc.pausas += Number(
          item.pausas_temporales || 0
        );

        acc.cambios += Number(
          item.cambios_profesional || 0
        );

        acc.noIniciaron += Number(
          item.no_iniciaron || 0
        );

        return acc;
      },
      {
        pacientes: 0,
        continuan: 0,
        abandonos: 0,
        pendientes: 0,
        altas: 0,
        pausas: 0,
        cambios: 0,
        noIniciaron: 0,
      }
    );
  }, [fidelizacion]);

  const totalEvaluables =
    totalFidelizacion.continuan +
    totalFidelizacion.abandonos;

  const continuidadCentro =
    totalEvaluables > 0
      ? (
          (
            totalFidelizacion.continuan /
            totalEvaluables
          ) *
          100
        ).toFixed(1)
      : "0.0";

  const abandonoCentro =
    totalEvaluables > 0
      ? (
          (
            totalFidelizacion.abandonos /
            totalEvaluables
          ) *
          100
        ).toFixed(1)
      : "0.0";

  /*
   * =========================================================
   * TABLA CONTROL
   * =========================================================
   */

  const TablaControl = ({
    titulo,
    items,
    tipo,
  }) => (
    <>
      <h4 style={{ marginTop: "1.2rem" }}>
        {titulo} ({items.length})
      </h4>

      <div className="pv-table-wrapper">
        <table className="pv-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Estado</th>
              <th>Última sesión</th>
              <th>Días</th>
              <th>30d</th>
              <th>90d</th>
              <th>Score</th>
              <th>Motivo</th>
            </tr>
          </thead>

          <tbody>
            {items.map((c) => (
              <tr
                key={
                  c.patient_id || c.id
                }
              >
                <td>
                  {c.nombre} {c.apellidos}
                </td>

                <td>
                  <Badge
                    tipo={
                      c.categoria || tipo
                    }
                  />
                </td>

                <td>
                  {fmtFecha(
                    c.ultima_sesion
                  )}
                </td>

                <td>
                  {fmtDias(
                    c.dias_desde_ultima
                  )}
                </td>

                <td>
                  {c.sesiones_30d ?? "—"}
                </td>

                <td>
                  {c.sesiones_90d ?? "—"}
                </td>

                <td
                  style={{
                    fontWeight: 700,
                  }}
                >
                  {c.score_riesgo ?? "—"}
                </td>

                <td
                  style={{
                    maxWidth: 320,
                  }}
                >
                  {c.motivo ||
                    c.motivo_riesgo ||
                    "—"}
                </td>
              </tr>
            ))}

            {items.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    textAlign: "center",
                    color: "#777",
                  }}
                >
                  No hay resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  /*
   * =========================================================
   * FIDELIZACIÓN / CARTERA
   * =========================================================
   */

  const renderFidelizacion = (
    esGlobal = false
  ) => (
    <>
      <div style={{ marginTop: 14 }}>
        <h4 style={{ marginBottom: 4 }}>
          {esGlobal
            ? "👥 Cartera global de pacientes"
            : `🔁 Fidelización — ${mes}`}
        </h4>

        <div
          style={{
            fontSize: 13,
            opacity: 0.75,
          }}
        >
          {esGlobal
            ? "Todos los pacientes históricos de cada profesional."
            : "Pacientes vinculados a cada profesional durante el mes seleccionado."}
        </div>
      </div>

      {/* RESUMEN CENTRO */}

      <div
        style={{
          marginTop: 14,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div className="pv-card">
          {esGlobal
            ? "Pacientes totales"
            : "Pacientes atendidos"}
          :{" "}
          <strong>
            {totalFidelizacion.pacientes}
          </strong>
        </div>

        <div className="pv-card">
          ✅ Continúan:{" "}
          <strong>
            {totalFidelizacion.continuan}
          </strong>
        </div>

        <div className="pv-card">
          🧊 Abandonos:{" "}
          <strong>
            {totalFidelizacion.abandonos}
          </strong>
        </div>

        <div className="pv-card">
          ⏳ Pendientes:{" "}
          <strong>
            {totalFidelizacion.pendientes}
          </strong>
        </div>

        {totalFidelizacion.altas > 0 && (
          <div className="pv-card">
            🎓 Altas:{" "}
            <strong>
              {totalFidelizacion.altas}
            </strong>
          </div>
        )}

        {totalFidelizacion.pausas > 0 && (
          <div className="pv-card">
            ⏸️ Pausas:{" "}
            <strong>
              {totalFidelizacion.pausas}
            </strong>
          </div>
        )}

        {totalFidelizacion.cambios > 0 && (
          <div className="pv-card">
            🔄 Cambios:{" "}
            <strong>
              {totalFidelizacion.cambios}
            </strong>
          </div>
        )}

        {totalFidelizacion.noIniciaron >
          0 && (
          <div className="pv-card">
            ⛔ No iniciaron:{" "}
            <strong>
              {totalFidelizacion.noIniciaron}
            </strong>
          </div>
        )}

        <div className="pv-card">
          Continuidad:{" "}
          <strong>
            {continuidadCentro} %
          </strong>
        </div>

        <div className="pv-card">
          Abandono:{" "}
          <strong>
            {abandonoCentro} %
          </strong>
        </div>
      </div>

      <p
        style={{
          marginTop: 10,
          fontSize: 13,
          opacity: 0.8,
        }}
      >
        {esGlobal ? (
          <>
            La cartera global muestra la situación actual de los
            pacientes. Continúa si tiene una próxima sesión
            programada con la misma profesional. Se considera
            abandono cuando no tiene próxima sesión y ha
            transcurrido un mes natural desde su última sesión.
            Puedes modificar manualmente el estado cuando la
            situación real del paciente no coincida con el
            cálculo automático.
          </>
        ) : (
          <>
            Esta vista muestra la fidelización correspondiente a{" "}
            <strong>{mes}</strong>. Continúa cuando el paciente
            tuvo una sesión posterior con la misma profesional,
            aunque fuese en otro mes. Se considera abandono cuando
            no volvió a tener ninguna sesión posterior y ha
            transcurrido un mes natural desde su última sesión.
            Los pendientes no entran en el cálculo de las tasas.
          </>
        )}
      </p>

      {mensajeEstado && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {mensajeEstado}
        </div>
      )}

      {/* PROFESIONALES */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 12,
          marginTop: 16,
        }}
      >
        {fidelizacion.map(
          (profesional) => (
            <button
              key={
                profesional.professional_id
              }
              type="button"
              onClick={() =>
                setProfesionalSeleccionado(
                  String(
                    profesional.professional_id
                  )
                )
              }
              className="pv-card"
              style={{
                textAlign: "left",
                cursor: "pointer",
                border:
                  String(
                    profesionalSeleccionado
                  ) ===
                  String(
                    profesional.professional_id
                  )
                    ? "2px solid currentColor"
                    : undefined,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 16,
                  marginBottom: 10,
                }}
              >
                {
                  profesional.professional_name
                }
              </div>

              <div>
                {esGlobal
                  ? "Pacientes totales"
                  : "Pacientes atendidos"}
                :{" "}
                <strong>
                  {
                    profesional.pacientes_atendidos
                  }
                </strong>
              </div>

              <div>
                Continúan:{" "}
                <strong>
                  {profesional.continuan}
                </strong>
              </div>

              <div>
                Abandonos:{" "}
                <strong>
                  {profesional.abandonos}
                </strong>
              </div>

              <div>
                Pendientes:{" "}
                <strong>
                  {profesional.pendientes}
                </strong>
              </div>

              {Number(
                profesional.altas_terapeuticas ||
                  0
              ) > 0 && (
                <div>
                  Altas:{" "}
                  <strong>
                    {
                      profesional.altas_terapeuticas
                    }
                  </strong>
                </div>
              )}

              {Number(
                profesional.pausas_temporales ||
                  0
              ) > 0 && (
                <div>
                  Pausas:{" "}
                  <strong>
                    {
                      profesional.pausas_temporales
                    }
                  </strong>
                </div>
              )}

              {Number(
                profesional.cambios_profesional ||
                  0
              ) > 0 && (
                <div>
                  Cambios:{" "}
                  <strong>
                    {
                      profesional.cambios_profesional
                    }
                  </strong>
                </div>
              )}

              {Number(
                profesional.no_iniciaron || 0
              ) > 0 && (
                <div>
                  No iniciaron:{" "}
                  <strong>
                    {
                      profesional.no_iniciaron
                    }
                  </strong>
                </div>
              )}

              <div
                style={{
                  marginTop: 8,
                }}
              >
                Continuidad:{" "}
                <strong>
                  {Number(
                    profesional.porcentaje_continuidad ||
                      0
                  ).toFixed(1)}
                  %
                </strong>
              </div>

              <div>
                Abandono:{" "}
                <strong>
                  {Number(
                    profesional.porcentaje_abandono ||
                      0
                  ).toFixed(1)}
                  %
                </strong>
              </div>
            </button>
          )
        )}
      </div>

      {/* DETALLE PROFESIONAL */}

      {profesionalActual ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "end",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 24,
            }}
          >
            <div>
              <h4
                style={{
                  marginBottom: 4,
                }}
              >
                Detalle —{" "}
                {
                  profesionalActual.professional_name
                }
              </h4>

              <div
                style={{
                  fontSize: 13,
                  opacity: 0.75,
                }}
              >
                {
                  pacientesFidelizacionFiltrados.length
                }{" "}
                pacientes visibles
              </div>
            </div>

            <label>
              Estado:
              <select
                value={
                  estadoFidelizacion
                }
                onChange={(e) =>
                  setEstadoFidelizacion(
                    e.target.value
                  )
                }
              >
                <option value="todos">
                  Todos
                </option>

                <option value="continua">
                  Continúa
                </option>

                <option value="pendiente">
                  Pendiente
                </option>

                <option value="abandono">
                  Abandono
                </option>

                {esGlobal && (
                  <>
                    <option value="alta_terapeutica">
                      Alta terapéutica
                    </option>

                    <option value="pausa_temporal">
                      Pausa temporal
                    </option>

                    <option value="cambio_profesional">
                      Cambio de profesional
                    </option>

                    <option value="no_inicio">
                      No inició
                    </option>
                  </>
                )}
              </select>
            </label>
          </div>

          <div
            className="pv-table-wrapper"
            style={{
              marginTop: 10,
            }}
          >
            <table className="pv-table">
              <thead>
                <tr>
                  <th>Paciente</th>
                  <th>Estado</th>
                  <th>Primera sesión</th>
                  <th>Última sesión</th>
                  <th>Próxima sesión</th>
                  <th>Fecha abandono</th>
                  <th>Realizadas</th>
                  <th>Programadas</th>
                </tr>
              </thead>

              <tbody>
                {pacientesFidelizacionFiltrados.map(
                  (item) => {
                    const key =
                      `${item.patient_id}-${item.professional_id}`;

                    const guardando =
                      guardandoEstado === key;

                    return (
                      <tr key={key}>
                        <td>
                          {item.nombre}{" "}
                          {item.apellidos}
                        </td>

                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexDirection:
                                "column",
                              gap: 6,
                              minWidth: esGlobal ? 175 : 120,
                            }}
                          >
                            <FidelizacionBadge
                              estado={
                                item.estado
                              }
                            />

                            {esGlobal && (
                              <>
                                <select
                                  value={
                                    item.estado_manual ||
                                    "automatico"
                                  }
                                  disabled={
                                    guardando
                                  }
                                  onChange={(e) =>
                                    cambiarEstadoPaciente(
                                      item,
                                      e.target
                                        .value
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    fontSize: 12,
                                  }}
                                >
                                  <option value="automatico">
                                    Automático
                                  </option>

                                  <option value="continua">
                                    Continúa
                                  </option>

                                  <option value="pendiente">
                                    Pendiente
                                  </option>

                                  <option value="abandono">
                                    Abandono
                                  </option>

                                  <option value="alta_terapeutica">
                                    Alta terapéutica
                                  </option>

                                  <option value="pausa_temporal">
                                    Pausa temporal
                                  </option>

                                  <option value="cambio_profesional">
                                    Cambio de profesional
                                  </option>

                                  <option value="no_inicio">
                                    No inició
                                  </option>
                                </select>

                                {item.manual_override && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      opacity: 0.65,
                                    }}
                                  >
                                    Estado manual
                                  </span>
                                )}

                                {guardando && (
                                  <span
                                    style={{
                                      fontSize: 11,
                                      opacity: 0.65,
                                    }}
                                  >
                                    Guardando...
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>

                        <td>
                          {fmtFecha(
                            item.primera_sesion
                          )}
                        </td>

                        <td>
                          {fmtFecha(
                            item.ultima_sesion
                          )}
                        </td>

                        <td>
                          {fmtFecha(
                            item.proxima_sesion
                          )}
                        </td>

                        <td>
                          {item.estado ===
                          "continua"
                            ? "—"
                            : fmtFecha(
                                item.fecha_abandono
                              )}
                        </td>

                        <td>
                          {
                            item.sesiones_hasta_fecha
                          }
                        </td>

                        <td>
                          {
                            item.sesiones_programadas ??
                            0
                          }
                        </td>
                      </tr>
                    );
                  }
                )}

                {pacientesFidelizacionFiltrados.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        textAlign:
                          "center",
                        color: "#777",
                      }}
                    >
                      No hay resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p style={{ marginTop: 18 }}>
          {esGlobal
            ? "No hay profesionales con pacientes."
            : "No hay profesionales con sesiones para este mes."}
        </p>
      )}
    </>
  );

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div className="clientes-tab">
      <h3>👥 Clientes — {mes}</h3>

      {/* FILTROS */}

      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "end",
        }}
      >
        <label>
          Mes:
          <input
            type="month"
            value={mes}
            onChange={(e) =>
              setMes(e.target.value)
            }
          />
        </label>

        <label>
          Buscar:
          <input
            type="text"
            value={q}
            onChange={(e) =>
              setQ(e.target.value)
            }
            placeholder="Nombre o apellidos..."
          />
        </label>

        <label>
          Umbral (días):
          <input
            type="number"
            min={1}
            max={365}
            value={umbralDias}
            onChange={(e) =>
              setUmbralDias(
                Number(e.target.value)
              )
            }
            style={{ width: 90 }}
          />
        </label>

        <label>
          Nuevo (días):
          <input
            type="number"
            min={1}
            max={60}
            value={nuevoDias}
            onChange={(e) =>
              setNuevoDias(
                Number(e.target.value)
              )
            }
            style={{ width: 90 }}
          />
        </label>
      </div>

      {/* TABS */}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 14,
          flexWrap: "wrap",
        }}
      >
        <button
          className={
            tab === "control"
              ? "btn-tab-active"
              : "btn-tab"
          }
          onClick={() =>
            setTab("control")
          }
        >
          🧭 Control
        </button>

        <button
          className={
            tab === "fidelizacion"
              ? "btn-tab-active"
              : "btn-tab"
          }
          onClick={() =>
            setTab("fidelizacion")
          }
        >
          🔁 Fidelización mes
        </button>

        <button
          className={
            tab === "cartera"
              ? "btn-tab-active"
              : "btn-tab"
          }
          onClick={() =>
            setTab("cartera")
          }
        >
          👥 Cartera global
        </button>

        <button
          className={
            tab === "creados"
              ? "btn-tab-active"
              : "btn-tab"
          }
          onClick={() =>
            setTab("creados")
          }
        >
          🆕 Creados ({totalCreados})
        </button>

        <button
          className={
            tab === "citas"
              ? "btn-tab-active"
              : "btn-tab"
          }
          onClick={() =>
            setTab("citas")
          }
        >
          📅 Con sesiones ({totalCitas})
        </button>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : error ? (
        <p className="error">
          {error}
        </p>
      ) : (
        <>
          {/* CONTROL */}

          {tab === "control" && (
            <>
              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div className="pv-card">
                  🆕 Nuevos:{" "}
                  <strong>
                    {totalNuevos}
                  </strong>
                </div>

                <div className="pv-card">
                  ⚠️ Riesgo:{" "}
                  <strong>
                    {totalRiesgo}
                  </strong>
                </div>

                <div className="pv-card">
                  🧊 Inactivos:{" "}
                  <strong>
                    {totalInactivos}
                  </strong>
                </div>

                <div className="pv-card">
                  ✅ Activos:{" "}
                  <strong>
                    {totalActivos}
                  </strong>
                </div>
              </div>

              <TablaControl
                titulo="🆕 Nuevos sin seguimiento"
                items={nuevosFiltrados}
                tipo="nuevo_sin_seguimiento"
              />

              <TablaControl
                titulo="⚠️ En riesgo (prioridad contacto)"
                items={riesgoFiltrado}
                tipo="riesgo"
              />

              <TablaControl
                titulo="🧊 Inactivos"
                items={
                  inactivosFiltrados
                }
                tipo="inactivo"
              />

              <TablaControl
                titulo="✅ Activos"
                items={activosFiltrados}
                tipo="activo"
              />
            </>
          )}

          {/* FIDELIZACIÓN */}

          {tab === "fidelizacion" &&
            renderFidelizacion(false)}

          {/* CARTERA GLOBAL */}

          {tab === "cartera" &&
            renderFidelizacion(true)}

          {/* CREADOS */}

          {tab === "creados" && (
            <>
              <h4>
                🆕 Clientes creados en el
                mes ({totalCreados})
              </h4>

              <div className="pv-table-wrapper">
                <table className="pv-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>
                        Fecha creación
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {creados
                      .filter(match)
                      .map((c) => (
                        <tr key={c.id}>
                          <td>
                            {c.nombre}{" "}
                            {c.apellidos}
                          </td>

                          <td>
                            {fmtFecha(
                              c.creado
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* CON SESIONES */}

          {tab === "citas" && (
            <>
              <h4
                style={{
                  marginTop: "1rem",
                }}
              >
                📅 Clientes con sesiones
                en el mes ({totalCitas})
              </h4>

              <div className="pv-table-wrapper">
                <table className="pv-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Nº sesiones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {citas
                      .filter(match)
                      .map((c) => (
                        <tr
                          key={
                            c.patient_id
                          }
                        >
                          <td>
                            {c.nombre}{" "}
                            {c.apellidos}
                          </td>

                          <td>
                            {c.sesiones}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}